import AppRouter from "@/AppRouter";
import { UserProvider } from "@/context/UserContext";
import { useTheme } from "@/context/useTheme";
import { useTranslationLoader } from "@/hooks/useTranslationLoader";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, Spin } from "antd";
import enUS from "antd/es/locale/en_US";
import hiIN from "antd/es/locale/hi_IN";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "@/store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: false },
  },
});

const getAntdLocale = (lng: string) => {
  switch (lng) {
    case "hi":
      return hiIN;
    case "en":
    default:
      return enUS;
  }
};

export const ConfigWrapper = () => {
  const { theme } = useTheme();
  const { i18n } = useTranslation();
  const { isLoading, isLoaded } = useTranslationLoader();
  const antdLocale = getAntdLocale(i18n.language);

  // Show loading spinner while translations are being loaded
  if (isLoading && !isLoaded) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spin size="large" tip="Loading translations..." />
      </div>
    );
  }

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        hashed: false,
        ...theme,
      }}
      componentSize="middle"
      // Global date format configuration
      datePicker={{
        format: "DD-MM-YYYY",
      }}
    >
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <UserProvider>
              <BrowserRouter>
                <AppRouter />
              </BrowserRouter>
            </UserProvider>
          </PersistGate>
        </Provider>
      </QueryClientProvider>
    </ConfigProvider>
  );
};
